import { retryWithBackoff } from "./retryWithBackoff.js"
import { getUploadSession, putUploadSession, deleteUploadSession } from "../lib/offlineDb.js"
import { cacheImageBlob, warmImageCache } from "../lib/imageCache.js"
import { errorMessage } from "./mutationResult.js"
import { stableUploadFile } from "./stableUploadFile.js"

/**
 * Phase 4 — direct-to-S3 upload with resumable multipart support.
 *
 * Flow:
 *  1. POST /api/files/presign/init — server generates the S3 key and either
 *     a single presigned PUT URL (small files) or a multipart upload
 *     session with one presigned URL per part (files >= 8MB).
 *  2. The browser normally PUTs bytes straight to S3. If the browser cannot
 *     reach storage, use the existing authenticated multipart upload API.
 *  3. POST /api/files/presign/complete — server finishes the multipart
 *     session (or verifies the single-PUT object exists) and creates the
 *     file document.
 *
 * Resumability: each part's ETag + a session id are persisted to
 * IndexedDB as they complete. If the upload is interrupted (tab closed,
 * connection dropped) and retried later with the same `resumeKey`, already
 * -completed parts are skipped entirely.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api"

const API_TIMEOUT_MS = 30000
// Camera images need longer than small JSON requests on mobile connections.
const uploadTimeout = (bytes) => Math.max(120000, Math.min(600000, Math.ceil(bytes / 32768) * 1000))

async function fetchWithTimeout(url, options = {}, timeoutMs = API_TIMEOUT_MS) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } catch (error) {
    if (error.name === "AbortError") {
      const timeoutError = new Error("The upload request timed out. Check your connection and retry the failed image.")
      timeoutError.name = "TimeoutError"
      timeoutError.status = 408
      throw timeoutError
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    ...extra,
  }
}

async function apiPost(path, body) {
  const response = await fetchWithTimeout(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    credentials: "include",
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    const err = new Error(errorMessage(error, `Request failed (${response.status}). Please try again.`))
    err.status = response.status
    throw err
  }
  const json = await response.json()
  return json.data
}

function sliceChunk(file, partNumber, chunkSize) {
  const start = (partNumber - 1) * chunkSize
  const end = Math.min(start + chunkSize, file.size)
  return file.slice(start, end)
}

async function putPart(url, chunk, onProgress) {
  return retryWithBackoff(async () => {
    const response = await storagePut(url, { method: "PUT", body: chunk }, chunk.size)
    if (!response.ok) {
      throw await storageFailure(response)
    }
    const etag = response.headers.get("ETag") || response.headers.get("etag")
    if (!etag) throw new Error("Storage did not return an upload confirmation (ETag). Please contact support to check the storage CORS settings.")
    if (onProgress) onProgress(chunk.size)
    return etag
  })
}

async function storageFailure(response) {
  const body = response.text ? await response.text().catch(() => "") : ""
  const code = body.match(/<Code>([^<]+)<\/Code>/)?.[1]
  const message = ["RequestExpired", "ExpiredToken", "SignatureDoesNotMatch"].includes(code)
    ? "The upload link expired or could not be verified. Please retry the image."
    : response.status === 403
      ? "Image storage denied this upload. Please retry; if it continues, contact your administrator to check upload permissions."
      : response.status === 413
        ? "This image is too large. Please choose a smaller image."
        : `Image storage could not accept the upload (${response.status}). Please retry the failed image.`
  return Object.assign(new Error(message), { status: response.status })
}

async function storagePut(url, options, bytes) {
  try {
    return await fetchWithTimeout(url, { ...options, credentials: "omit" }, uploadTimeout(bytes))
  } catch (error) {
    if (error.name === "TypeError" && navigator.onLine !== false) {
      const failure = new Error("Cannot reach image storage. Check your connection and try again. If this continues, contact support to check storage access and CORS settings.")
      failure.name = "UploadNetworkError"
      failure.status = 503
      throw failure
    }
    throw error
  }
}

async function uploadThroughApi(file, meta) {
  const form = new FormData()
  form.append("file", file, file.name)
  form.append("siteId", meta.siteId)
  form.append("entityType", meta.entityType)
  form.append("entityId", meta.entityId)
  if (meta.reportId) form.append("reportId", meta.reportId)
  form.append("filterData", JSON.stringify(meta.filterData || {}))
  form.append("metadata", JSON.stringify(meta.metadata || {}))
  // The browser supplies the multipart boundary; do not set Content-Type.
  const response = await fetchWithTimeout(`${API_BASE_URL}/files/upload`, {
    method: "POST", headers: authHeaders(), credentials: "include", body: form,
  }, uploadTimeout(file.size))
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw Object.assign(new Error(errorMessage(payload, "Could not upload the image through the server. Please retry.")), { status: response.status })
  }
  if (!payload.data?._id && !payload.data?.id) throw new Error("The server did not confirm this image upload. Please refresh the section before retrying.")
  return payload.data
}

/**
 * Uploads a single (non-multipart) file directly to S3 via one presigned
 * PUT URL.
 */
async function uploadSinglePut({ file, uploadUrl, onProgress }) {
  await retryWithBackoff(async () => {
    const response = await storagePut(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }, file.size)
    if (!response.ok) {
      throw await storageFailure(response)
    }
    if (onProgress) onProgress(file.size)
  })
}

/**
 * Uploads a large file as a resumable multipart session, persisting
 * completed part ETags to IndexedDB under `resumeKey` so a later retry can
 * skip parts that already succeeded.
 */
async function uploadMultipart({ file, session, resumeKey, onProgress }) {
  const { uploadId, key, chunkSize, parts: initialPartUrls } = session

  const existing = resumeKey ? await getUploadSession(resumeKey) : null
  const completedParts = new Map(
    (existing?.key === key && existing?.uploadId === uploadId ? existing.completedParts || [] : []).map((part) => [part.partNumber, part.etag]),
  )

  let partUrlsByNumber = new Map(initialPartUrls.map((p) => [p.partNumber, p.url]))

  for (const { partNumber } of initialPartUrls) {
    if (completedParts.has(partNumber)) {
      if (onProgress) onProgress(sliceChunk(file, partNumber, chunkSize).size)
      continue
    }

    const url = partUrlsByNumber.get(partNumber)
    const chunk = sliceChunk(file, partNumber, chunkSize)
    const etag = await putPart(url, chunk, onProgress)
    completedParts.set(partNumber, etag)

    if (resumeKey) {
      await putUploadSession({
        id: resumeKey,
        key,
        uploadId,
        chunkSize,
        completedParts: Array.from(completedParts, ([number, tag]) => ({ partNumber: number, etag: tag })),
      })
    }
  }

  const parts = Array.from(completedParts, ([partNumber, etag]) => ({ partNumber, etag }))

  return { uploadId, key, parts }
}

/**
 * Uploads `file` directly to S3 (single PUT or resumable multipart,
 * decided by the server) and then completes the file record.
 *
 * @param {File} file
 * @param {object} meta - { siteId, reportId, entityType, entityId, filterData, metadata }
 * @param {object} options - { resumeKey, onProgress }
 * @returns the created file record, matching the shape the legacy
 *          multipart/form-data endpoints returned.
 */
export async function uploadFileDirectToS3(file, meta, options = {}) {
  const { resumeKey, onProgress } = options
  file = await stableUploadFile(file)
  let reportedBytes = 0
  const reportProgress = (bytes) => {
    reportedBytes += bytes
    if (onProgress) onProgress(bytes)
  }

  const initData = await apiPost("/files/presign/init", {
    originalName: file.name,
    mimeType: file.type || "application/octet-stream",
    fileSize: file.size,
    entityType: meta.entityType,
    entityId: meta.entityId,
    siteId: meta.siteId,
    filterData: meta.filterData,
    metadata: meta.metadata,
  })

  let completePayload = {
    key: initData.key,
    entityType: meta.entityType,
    entityId: meta.entityId,
    siteId: meta.siteId,
    reportId: meta.reportId,
    filterData: meta.filterData,
    metadata: meta.metadata,
    originalName: file.name,
    mimeType: file.type || "application/octet-stream",
    fileSize: file.size,
  }

  let record
  try {
    if (initData.multipart) {
      const { uploadId, parts } = await uploadMultipart({ file, session: initData, resumeKey, onProgress: reportProgress })
      completePayload = { ...completePayload, uploadId, parts }
    } else {
      await uploadSinglePut({ file, uploadUrl: initData.uploadUrl, onProgress: reportProgress })
    }
  } catch (error) {
    // Only an unreadable browser network response from storage qualifies.
    // HTTP rejections, timeouts, offline operation and completion failures
    // must never trigger a second upload via the API.
    if (error.name !== "UploadNetworkError" || navigator.onLine === false) throw error
    record = await uploadThroughApi(file, meta)
    reportProgress(Math.max(0, file.size - reportedBytes))
    if (initData.multipart) {
      await apiPost("/files/presign/abort", { key: initData.key, uploadId: initData.uploadId }).catch(() => {})
    }
  }

  // The API fallback creates the record itself. Never call complete again.
  if (!record) record = await apiPost("/files/presign/complete", completePayload)
  // A local cache failure must not turn a confirmed server upload into a
  // failed upload that the user resends and duplicates.
  if (record.mimeType && record.mimeType !== file.type) {
    // The server can convert phone HEIC/HEIF images to JPEG. Cache the
    // converted image, rather than associating the original bytes with it.
    await warmImageCache([record]).catch(() => {})
  } else {
    await cacheImageBlob(record._id || record.id, file, record.fileUrl).catch(() => {})
  }
  if (resumeKey) await deleteUploadSession(resumeKey).catch(() => {})
  return record
}
